import React, { useEffect, useState, useRef } from "react";
import {
  deleteRule,
  Rule,
  findAll,
  getRule,
  exportRules,
  importRules,
  deleteAllRules,
  getRuleMatches,
  RuleMatchesResponse,
  RuleSourceType,
  createRule,
  CreateRuleDTO,
  RulePatternTarget,
} from "../lib/rulesLib";
import { formatDate } from "../lib/utils";
import { useAuthContext } from "../lib/authLib";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSnackbar } from "../contexts/SnackbarContext";

type SortField = "createdAt" | "sourceType" | "scopeTargetType" | "ruleAction";
type SortDirection = "asc" | "desc";

const RulesTable = (): React.ReactElement => {
  const { user } = useAuthContext();
  const { showSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userId = searchParams.get("userId");
  const filterParam = searchParams.get("filter") || "";
  const [rules, setRules] = useState<Rule[]>([]);
  const [selectedRule, setSelectedRule] = useState<Rule | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ruleMatches, setRuleMatches] = useState<RuleMatchesResponse | null>(
    null,
  );
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(filterParam);
  const rowRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({});
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [importResponse, setImportResponse] = useState<unknown>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const gatedFileInputRef = useRef<HTMLInputElement>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCreatingRule, setIsCreatingRule] = useState(false);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [isAliasModalOpen, setIsAliasModalOpen] = useState(false);
  const [isDomainModalOpen, setIsDomainModalOpen] = useState(false);
  const [aliasInput, setAliasInput] = useState("");
  const [domainInput, setDomainInput] = useState("");
  const addMenuRef = useRef<HTMLDivElement>(null);

  // Update search query when URL param changes
  useEffect(() => {
    setSearchQuery(filterParam);
  }, [filterParam]);

  useEffect(() => {
    setIsLoading(true);
    findAll(userId ? { userId: userId } : {})
      .then((data) => {
        setRules(data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
      });
  }, [userId]);

  // Open rule details modal if ruleId is in URL
  useEffect(() => {
    const ruleId = searchParams.get("ruleId");
    if (ruleId) {
      // If the rule is in the list, scroll to it
      const found = rules.find((r) => r.id === ruleId);
      if (found && rowRefs.current[ruleId]) {
        rowRefs.current[ruleId]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      // Always fetch the rule details for the modal
      getRule(ruleId)
        .then((rule) => {
          setSelectedRule(rule);
          setIsModalOpen(true);
          // Fetch rule matches
          fetchRuleMatches(ruleId);
        })
        .catch(() => {
          setSelectedRule(null);
          setIsModalOpen(false);
        });
    }
  }, [searchParams, rules]);

  // Close modal on Escape key
  useEffect(() => {
    if (!isModalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsModalOpen(false);
        setRuleMatches(null);
        const params = new URLSearchParams(searchParams);
        params.delete("ruleId");
        navigate({ search: params.toString() }, { replace: false });
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isModalOpen, searchParams, navigate]);

  // Close ... menu if clicking outside
  useEffect(() => {
    if (!openMenuId) return;
    function handleClick(e: MouseEvent) {
      if (typeof openMenuId === "string") {
        const menuEl = menuRefs.current[openMenuId];
        if (menuEl && !menuEl.contains(e.target as Node)) {
          setOpenMenuId(null);
        }
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [openMenuId]);

  // Close add menu if clicking outside
  useEffect(() => {
    if (!isAddMenuOpen) return;
    function handleClick(e: MouseEvent) {
      if (
        addMenuRef.current &&
        !addMenuRef.current.contains(e.target as Node)
      ) {
        setIsAddMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isAddMenuOpen]);

  // Close alias modal on Escape key
  useEffect(() => {
    if (!isAliasModalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsAliasModalOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isAliasModalOpen]);

  // Close domain modal on Escape key
  useEffect(() => {
    if (!isDomainModalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsDomainModalOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDomainModalOpen]);

  const getHeaderText = () => {
    if (user?.isSuperAdmin) {
      if (userId) {
        return (
          <span>
            Rules for {user.email} (
            <a href="/rules" className="text-blue-500 hover:text-blue-700">
              show all
            </a>
            )
          </span>
        );
      }
      return "Rules (admin view)";
    }
    return "Rules";
  };

  const handleDelete = (id: string) => {
    console.log(`deleting rule ${id}`);
    deleteRule(id)
      .then(() => {
        // Remove the rule from the state
        setRules((currentRules) =>
          currentRules.filter((rule) => rule.id !== id),
        );
      })
      .catch(console.error);
  };

  const fetchRuleMatches = async (ruleId: string) => {
    setIsLoadingMatches(true);
    try {
      const matches = await getRuleMatches(ruleId);
      setRuleMatches(matches);
    } catch (error) {
      console.error("Failed to fetch rule matches:", error);
      setRuleMatches(null);
    } finally {
      setIsLoadingMatches(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new field and default to descending
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    const params = new URLSearchParams(searchParams);
    if (query.trim()) {
      params.set("filter", query);
    } else {
      params.delete("filter");
    }
    navigate({ search: params.toString() }, { replace: false });
  };

  const handleReset = () => {
    setSearchQuery("");
    const params = new URLSearchParams(searchParams);
    params.delete("filter");
    navigate({ search: params.toString() }, { replace: false });
  };

  const filterRules = (rules: Rule[], query: string): Rule[] => {
    if (!query.trim()) return rules;

    const terms = query.split(/\s+/).filter((term) => term.trim());
    if (terms.length === 0) return rules;

    return rules.filter((rule) => {
      return terms.every((term) => {
        // Check for user: prefix filter
        if (term.startsWith("user:")) {
          const userSearchTerm = term.substring(5); // Remove 'user:' prefix

          // First check if rule is user-scoped
          if (rule.scopeTargetType !== "user") {
            return false;
          }

          // If no search term after user:, just return user-scoped rules
          if (!userSearchTerm.trim()) {
            return true;
          }

          // Check if scopeTargetDetails exists and matches the search term
          if (rule.scopeTargetDetails) {
            const searchLower = userSearchTerm.toLowerCase();
            return (
              rule.scopeTargetDetails.email
                .toLowerCase()
                .includes(searchLower) ||
              rule.scopeTargetDetails.firstName
                .toLowerCase()
                .includes(searchLower) ||
              rule.scopeTargetDetails.lastName
                .toLowerCase()
                .includes(searchLower)
            );
          }

          return false;
        }

        // Check for global: prefix filter
        if (term.startsWith("global:")) {
          // Return only global-scoped rules
          return rule.scopeTargetType === "global" || !rule.scopeTargetType;
        }

        // Check if term looks like a UUID (contains hyphens and is alphanumeric)
        const uuidPattern = /^[a-f0-9-]+$/i;
        if (uuidPattern.test(term)) {
          return rule.id.toLowerCase().includes(term.toLowerCase());
        }

        // Check pattern values for LIKE search
        return rule.patterns.some(
          (pattern) =>
            pattern.value.toLowerCase().includes(term.toLowerCase()) ||
            pattern.target.toLowerCase().includes(term.toLowerCase()),
        );
      });
    });
  };

  const filteredRules = filterRules(rules, searchQuery);

  const sortedRules = [...filteredRules].sort((a, b) => {
    let comparison = 0;

    switch (sortField) {
      case "createdAt":
        comparison =
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
      case "sourceType":
        comparison = a.sourceType.localeCompare(b.sourceType);
        break;
      case "scopeTargetType":
        const scopeA = a.scopeTargetType || "global";
        const scopeB = b.scopeTargetType || "global";
        comparison = scopeA.localeCompare(scopeB);
        break;
      case "ruleAction":
        comparison = a.ruleAction.localeCompare(b.ruleAction);
        break;
      default:
        break;
    }

    return sortDirection === "asc" ? comparison : -comparison;
  });

  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;

    return (
      <span className="inline-block w-4 text-center">
        {sortDirection === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  const formatPattern = (pattern: { target: string; value: string }) => {
    const labels: { [key: string]: string } = {
      "sender-address": "From",
      "sender-domain": "Domain",
      subject: "Subject",
      "header-key-exists": "Header",
      "recipient-address": "To",
      "recipient-domain": "To Domain",
    };
    return (
      <span className="inline-flex items-center">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1">
          {labels[pattern.target] || pattern.target}
        </span>
        <span className="text-sm">
          {pattern.target === "sender-domain" ||
          pattern.target === "recipient-domain"
            ? `*@${pattern.value}`
            : pattern.value}
        </span>
      </span>
    );
  };

  const getSourceTypeColorAndLabel = (sourceType: string) => {
    switch (sourceType) {
      case RuleSourceType.CAP:
        return {
          style:
            "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300",
          label: "CAP",
        };
      case RuleSourceType.INBOX_LABEL:
        return {
          style:
            "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
          label: "Inbox Label",
        };
      case RuleSourceType.DASHBOARD:
        return {
          style:
            "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300",
          label: "Dashboard",
        };
      case RuleSourceType.SENT_MESSAGE:
        return {
          style:
            "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
          label: "Sent Message",
        };
      case RuleSourceType.SENT_MESSAGE_INITIAL_SYNC:
        return {
          style:
            "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
          label: "Sent Message (Initial Sync)",
        };
      case RuleSourceType.IMPORT:
        return {
          style:
            "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
          label: "Imported",
        };
      case RuleSourceType.ONBOARDING_CORPORATE_DOMAIN:
        return {
          style:
            "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
          label: "Corporate Domain",
        };
      case RuleSourceType.SYSTEM_TRANSACTIONAL_MAIL:
        return {
          style:
            "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200",
          label: "System transactional mail",
        };
      default:
        return {
          style:
            "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
          label: "Unknown",
        };
    }
  };

  const getActionDisplayText = (action: string) => {
    switch (action) {
      case "challenge":
        return "Blocked";
      case "allow":
        return "Allowed";
      case "filter":
        return "Filtered";
      default:
        return action;
    }
  };

  const handleExport = async () => {
    try {
      await exportRules();
    } catch (error) {
      console.error("Failed to export rules:", error);
    }
  };

  const handleImport = async (file: File, format?: "gated") => {
    try {
      const response = await importRules(file, format);
      setImportResponse(response);
      // Refresh the rules list
      const data = await findAll(userId ? { userId: userId } : {});
      setRules(data);
    } catch (error) {
      console.error("Failed to import rules:", error);
      setImportResponse({ error: "Failed to import rules" });
    }
  };

  const handleDeleteAll = async () => {
    try {
      const response = await deleteAllRules();
      // Refresh the rules list
      const data = await findAll(userId ? { userId: userId } : {});
      setRules(data);
      setIsDeleteModalOpen(false);

      // Show confirmation snackbar
      if (response && response.deletedCount !== undefined) {
        if (response.deletedCount > 0) {
          showSnackbar(
            `Successfully deleted ${response.deletedCount} rules`,
            "success",
          );
        } else {
          showSnackbar(response.message || "No rules were deleted", "info");
        }
      } else {
        showSnackbar("Rule deletion completed", "success");
      }
    } catch (error) {
      console.error("Failed to delete all rules:", error);
      showSnackbar("Failed to delete rules", "error");
    }
  };

  // Helper functions to detect search pattern type
  const isEmailPattern = (query: string): boolean => {
    // Basic email pattern: anything@anything.anything
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(query.trim());
  };

  const isDomainPattern = (query: string): boolean => {
    // Basic domain pattern: [a-z0-9-]*.[a-z]*
    const domainRegex = /^[a-z0-9-]+\.[a-z]+$/i;
    return domainRegex.test(query.trim());
  };

  const handleQuickAction = async (
    searchTerm: string,
    action: "challenge" | "filter" | "allow",
    type:
      | "domain"
      | "sender"
      | "subject"
      | "recipient-domain"
      | "recipient-address",
  ) => {
    setIsCreatingRule(true);
    try {
      const ruleData: CreateRuleDTO = {
        messageId: "", // Empty string for rules created from search
        scopeTargetType: "user",
        scopeTargetId: user?.id || null,
        ruleAction: action,
        patterns: [],
      };

      // Set patterns based on type
      if (type === "domain") {
        ruleData.patterns = [
          {
            target: RulePatternTarget.SENDER_DOMAIN,
            value: searchTerm.trim(),
          },
        ];
      } else if (type === "sender") {
        ruleData.patterns = [
          {
            target: RulePatternTarget.SENDER_ADDRESS,
            value: searchTerm.trim(),
          },
        ];
      } else if (type === "recipient-domain") {
        ruleData.patterns = [
          {
            target: RulePatternTarget.RECIPIENT_DOMAIN,
            value: searchTerm.trim(),
          },
        ];
      } else if (type === "recipient-address") {
        ruleData.patterns = [
          {
            target: RulePatternTarget.RECIPIENT_ADDRESS,
            value: searchTerm.trim(),
          },
        ];
      } else {
        // subject
        ruleData.patterns = [
          {
            target: RulePatternTarget.SUBJECT,
            value: searchTerm.trim(),
          },
        ];
      }

      await createRule(ruleData);
      showSnackbar(
        `Rule created: ${action === "challenge" ? "Block" : "Allow"} ${type} "${searchTerm}"`,
        "success",
      );

      // Refetch rules and retain search query
      const data = await findAll(userId ? { userId: userId } : {});
      setRules(data);
    } catch (error) {
      console.error("Failed to create rule:", error);
      showSnackbar("Failed to create rule. Please try again.", "error");
    } finally {
      setIsCreatingRule(false);
    }
  };

  const handleCreateAliasRule = async () => {
    if (!aliasInput.trim()) {
      showSnackbar("Please enter an email address or domain", "error");
      return;
    }

    setIsCreatingRule(true);
    try {
      const trimmedInput = aliasInput.trim();
      const isEmail = trimmedInput.includes("@");

      const ruleData: CreateRuleDTO = {
        messageId: "",
        scopeTargetType: "user",
        scopeTargetId: user?.id || null,
        ruleAction: "allow",
        patterns: [
          {
            target: isEmail
              ? RulePatternTarget.RECIPIENT_ADDRESS
              : RulePatternTarget.RECIPIENT_DOMAIN,
            value: trimmedInput,
          },
        ],
      };

      await createRule(ruleData);
      showSnackbar(
        `Rule created: Allow emails to ${isEmail ? "address" : "domain"} "${trimmedInput}"`,
        "success",
      );

      // Refetch rules
      const data = await findAll(userId ? { userId: userId } : {});
      setRules(data);

      // Close modal and reset input
      setIsAliasModalOpen(false);
      setAliasInput("");
    } catch (error) {
      console.error("Failed to create rule:", error);
      showSnackbar("Failed to create rule. Please try again.", "error");
    } finally {
      setIsCreatingRule(false);
    }
  };

  const handleCreateDomainRule = async () => {
    if (!domainInput.trim()) {
      showSnackbar("Please enter a domain", "error");
      return;
    }

    setIsCreatingRule(true);
    try {
      const trimmedDomain = domainInput.trim().replace(/^@/, ""); // Remove @ if present

      const ruleData: CreateRuleDTO = {
        messageId: "",
        scopeTargetType: "user",
        scopeTargetId: user?.id || null,
        ruleAction: "allow",
        patterns: [
          {
            target: RulePatternTarget.SENDER_DOMAIN,
            value: trimmedDomain,
          },
        ],
      };

      await createRule(ruleData);
      showSnackbar(
        `Rule created: Allow emails from domain "@${trimmedDomain}"`,
        "success",
      );

      // Refetch rules
      const data = await findAll(userId ? { userId: userId } : {});
      setRules(data);

      // Close modal and reset input
      setIsDomainModalOpen(false);
      setDomainInput("");
    } catch (error) {
      console.error("Failed to create rule:", error);
      showSnackbar("Failed to create rule. Please try again.", "error");
    } finally {
      setIsCreatingRule(false);
    }
  };

  return (
    <React.Fragment>
      {/* @ts-expect-error - TypeScript incorrectly infers div children type */}
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-center">{getHeaderText()}</h2>
          <div className="flex gap-2">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".json"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleImport(file);
                }
                e.target.value = "";
              }}
            />
            <input
              type="file"
              ref={gatedFileInputRef}
              className="hidden"
              accept=".json"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleImport(file, "gated");
                }
                e.target.value = "";
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Import
            </button>
            <button
              onClick={() => gatedFileInputRef.current?.click()}
              className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Import (Gated)
            </button>
            <button
              onClick={handleExport}
              className="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Export
            </button>
            <button
              onClick={() => setIsDeleteModalOpen(true)}
              className="px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Delete All
            </button>
          </div>
        </div>

        {/* Search Box */}
        <div className="mb-4 flex items-center gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
          {searchQuery && (
            <button
              onClick={handleReset}
              className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 underline"
            >
              Reset
            </button>
          )}
          {/* Add Rule Button with Dropdown */}
          <div className="relative" ref={addMenuRef}>
            <button
              onClick={() => setIsAddMenuOpen(!isAddMenuOpen)}
              className="w-10 h-10 flex items-center justify-center text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 text-xl font-bold"
              title="Create new rule"
            >
              +
            </button>
            {isAddMenuOpen && (
              <div className="absolute right-0 mt-2 w-72 rounded-md shadow-lg bg-white dark:bg-gray-700 ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1">
                  <button
                    className="block w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => {
                      setIsAddMenuOpen(false);
                      setIsAliasModalOpen(true);
                    }}
                  >
                    <div className="font-medium">
                      Allow email forwarded via alias/list
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      For mailing lists or Gmail aliases
                    </div>
                  </button>
                  <button
                    className="block w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => {
                      setIsAddMenuOpen(false);
                      setIsDomainModalOpen(true);
                    }}
                  >
                    <div className="font-medium">Allow email from a domain</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Allow all emails from a specific domain
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Search Results Info */}
        {searchQuery && (
          <div className="mb-4">
            {isLoading ? (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <span>Loading rules...</span>
              </div>
            ) : filteredRules.length === 0 ? (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-4 flex-wrap">
                  <span>
                    No matches found for "{searchQuery}"{" "}
                    <a
                      href={`/messages?filter=${searchQuery}`}
                      className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 underline"
                    >
                      search messages instead
                    </a>
                  </span>
                  <div className="flex items-center gap-2">
                    {isEmailPattern(searchQuery) ? (
                      <>
                        <button
                          onClick={() =>
                            handleQuickAction(searchQuery, "filter", "sender")
                          }
                          disabled={isCreatingRule}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Block sender {searchQuery}
                        </button>
                        <button
                          onClick={() =>
                            handleQuickAction(searchQuery, "allow", "sender")
                          }
                          disabled={isCreatingRule}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Allow sender {searchQuery}
                        </button>
                        <button
                          onClick={() =>
                            handleQuickAction(
                              searchQuery,
                              "filter",
                              "recipient-address",
                            )
                          }
                          disabled={isCreatingRule}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Block recipient {searchQuery}
                        </button>
                        <button
                          onClick={() =>
                            handleQuickAction(
                              searchQuery,
                              "allow",
                              "recipient-address",
                            )
                          }
                          disabled={isCreatingRule}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Allow recipient {searchQuery}
                        </button>
                      </>
                    ) : isDomainPattern(searchQuery) ? (
                      <>
                        <button
                          onClick={() =>
                            handleQuickAction(searchQuery, "filter", "domain")
                          }
                          disabled={isCreatingRule}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Block sender domain @{searchQuery}
                        </button>
                        <button
                          onClick={() =>
                            handleQuickAction(searchQuery, "allow", "domain")
                          }
                          disabled={isCreatingRule}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Allow sender domain @{searchQuery}
                        </button>
                        <button
                          onClick={() =>
                            handleQuickAction(
                              searchQuery,
                              "filter",
                              "recipient-domain",
                            )
                          }
                          disabled={isCreatingRule}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Block recipient domain @{searchQuery}
                        </button>
                        <button
                          onClick={() =>
                            handleQuickAction(
                              searchQuery,
                              "allow",
                              "recipient-domain",
                            )
                          }
                          disabled={isCreatingRule}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Allow recipient domain @{searchQuery}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() =>
                            handleQuickAction(searchQuery, "filter", "subject")
                          }
                          disabled={isCreatingRule}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Block subject matching {searchQuery}
                        </button>
                        <button
                          onClick={() =>
                            handleQuickAction(searchQuery, "allow", "subject")
                          }
                          disabled={isCreatingRule}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Allow subject matching {searchQuery}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <span>
                  Showing {filteredRules.length} of {rules.length} rules
                  {filteredRules.length !== rules.length &&
                    ` matching "${searchQuery}"`}
                </span>
              </div>
            )}
          </div>
        )}

        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th
                className="px-4 py-2 text-left cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 whitespace-nowrap min-w-[110px]"
                onClick={() => handleSort("createdAt")}
              >
                <div className="flex items-center">
                  <span>Created</span>
                  {getSortIndicator("createdAt")}
                </div>
              </th>
              <th
                className="px-4 py-2 text-left cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 whitespace-nowrap min-w-[100px]"
                onClick={() => handleSort("sourceType")}
              >
                <div className="flex items-center">
                  <span>Source</span>
                  {getSortIndicator("sourceType")}
                </div>
              </th>
              <th
                className="px-4 py-2 text-left cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 whitespace-nowrap min-w-[100px]"
                onClick={() => handleSort("scopeTargetType")}
              >
                <div className="flex items-center">
                  <span>Scope</span>
                  {getSortIndicator("scopeTargetType")}
                </div>
              </th>
              <th
                className="px-4 py-2 text-left cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 whitespace-nowrap min-w-[100px]"
                onClick={() => handleSort("ruleAction")}
              >
                <div className="flex items-center">
                  <span>Action</span>
                  {getSortIndicator("ruleAction")}
                </div>
              </th>
              <th className="px-4 py-2 text-left whitespace-nowrap min-w-[200px]">
                Patterns
              </th>
              <th className="px-4 py-2 w-[48px]"></th>
            </tr>
          </thead>
          <tbody>
            {sortedRules.map((rule) => (
              <tr
                key={rule.id}
                ref={(el) => {
                  rowRefs.current[rule.id] = el;
                }}
                className="border-b hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                onClick={async (e) => {
                  // Prevent row click if ... menu or its dropdown is clicked
                  if (
                    (e.target as HTMLElement).closest(
                      'button, [role="menu"], .dropdown, .menu',
                    )
                  )
                    return;
                  const params = new URLSearchParams(searchParams);
                  params.set("ruleId", rule.id);
                  navigate({ search: params.toString() }, { replace: false });
                  try {
                    const fetchedRule = await getRule(rule.id);
                    setSelectedRule(fetchedRule);
                    setIsModalOpen(true);
                    // Fetch rule matches
                    fetchRuleMatches(rule.id);
                  } catch {
                    setSelectedRule(null);
                    setIsModalOpen(false);
                  }
                }}
              >
                <td className="px-4 py-2 text-sm text-gray-500">
                  {formatDate(rule.createdAt)}
                </td>
                <td className="px-4 py-2">
                  <span
                    className={`text-xs font-medium px-2.5 py-0.5 rounded whitespace-nowrap ${getSourceTypeColorAndLabel(rule.sourceType).style}`}
                  >
                    {getSourceTypeColorAndLabel(rule.sourceType).label}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <span
                    className={`text-xs font-medium px-2.5 py-1 rounded inline-block ${
                      rule.scopeTargetType === "user"
                        ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300"
                        : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                    }`}
                  >
                    <div className="text-center">
                      {rule.scopeTargetType || "global"}
                    </div>
                    {user?.isSuperAdmin && rule.scopeTargetDetails && (
                      <div className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                        {rule.scopeTargetDetails.firstName}{" "}
                        {rule.scopeTargetDetails.lastName}
                        <br />
                        {rule.scopeTargetDetails.email}
                      </div>
                    )}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <span
                    className={`text-xs font-medium px-1 py-0.5 rounded inline-block ${
                      rule.ruleAction === "challenge"
                        ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                        : rule.ruleAction === "filter"
                          ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                          : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                    }`}
                  >
                    {rule.ruleAction}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <div className="space-y-1 relative">
                    {rule.patterns.map((pattern, index) => (
                      <div
                        key={`${rule.id}-pattern-${index}`}
                        className="relative"
                        style={{ maxWidth: 400 }}
                      >
                        {/* This overlay expands automatically with parent width, which is clamped */}
                        <div className="absolute inset-0 bg-gray-50 dark:bg-gray-700 rounded pointer-events-none" />
                        <div
                          className="relative px-2 py-1 min-w-[200px] max-w-full overflow-x-auto whitespace-nowrap"
                          style={{
                            maskImage:
                              "linear-gradient(to right, black 85%, transparent 97%)",
                            WebkitMaskImage:
                              "linear-gradient(to right, black 85%, transparent 97%)",
                            maxWidth: "400px",
                            minWidth: "200px",
                          }}
                        >
                          {formatPattern(pattern)}
                        </div>
                        {/* Fade overlay for environments where mask-image isn't supported */}
                        <div
                          className="pointer-events-none absolute right-0 top-0 h-full w-12 rounded-r"
                          style={{
                            background:
                              "linear-gradient(to right, rgba(255,255,255,0.0), var(--tw-bg-opacity, 1) 80%)",
                            backgroundColor: "inherit",
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-2 relative">
                  <button
                    className="text-gray-500 hover:text-gray-700 font-bold w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center justify-center transition-colors"
                    onClick={() =>
                      setOpenMenuId(openMenuId === rule.id ? null : rule.id)
                    }
                  >
                    ⋮
                  </button>
                  {openMenuId === rule.id && (
                    <div
                      ref={(el) => {
                        menuRefs.current[rule.id] = el;
                      }}
                      className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-700 ring-1 ring-black ring-opacity-5 z-10"
                    >
                      <div className="py-1">
                        <button
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                          onClick={async () => {
                            setOpenMenuId(null);
                            const params = new URLSearchParams(searchParams);
                            params.set("ruleId", rule.id);
                            navigate(
                              { search: params.toString() },
                              { replace: false },
                            );
                            try {
                              const fetchedRule = await getRule(rule.id);
                              setSelectedRule(fetchedRule);
                              setIsModalOpen(true);
                              // Fetch rule matches
                              fetchRuleMatches(rule.id);
                            } catch {
                              setSelectedRule(null);
                              setIsModalOpen(false);
                            }
                          }}
                        >
                          Details
                        </button>
                        <button
                          className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600"
                          onClick={() => {
                            handleDelete(rule.id);
                            setOpenMenuId(null);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {isLoading ? (
          <p className="text-center text-gray-500 mt-4">Loading...</p>
        ) : (
          rules.length === 0 && (
            <p className="text-center text-gray-500 mt-4">No rules found.</p>
          )
        )}

        {/* Placeholder Modal */}
        {isModalOpen && selectedRule && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
            onClick={() => {
              setIsModalOpen(false);
              setRuleMatches(null);
              const params = new URLSearchParams(searchParams);
              params.delete("ruleId");
              navigate({ search: params.toString() }, { replace: false });
            }}
          >
            <div
              className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-2xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Rule Details</h3>
                <button
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  onClick={() => {
                    setIsModalOpen(false);
                    setRuleMatches(null);
                    const params = new URLSearchParams(searchParams);
                    params.delete("ruleId");
                    navigate({ search: params.toString() }, { replace: false });
                  }}
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Summary line */}
              <div className="mb-6">
                <p className="text-lg">
                  <span
                    className={`text-xs font-medium px-1 py-0.5 rounded inline-block ${
                      selectedRule.ruleAction === "challenge"
                        ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                        : selectedRule.ruleAction === "filter"
                          ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                          : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                    }`}
                  >
                    {selectedRule.ruleAction === "challenge"
                      ? "Block"
                      : selectedRule.ruleAction === "allow"
                        ? "Allow"
                        : selectedRule.ruleAction === "filter"
                          ? "Filter"
                          : selectedRule.ruleAction}
                  </span>
                  {" messages "}
                  {selectedRule.patterns &&
                    selectedRule.patterns.length > 0 &&
                    (() => {
                      const pattern = selectedRule.patterns[0];
                      return (
                        <div className="inline-block bg-gray-50 dark:bg-gray-700 rounded px-2 py-1">
                          {formatPattern(pattern)}
                        </div>
                      );
                    })()}
                </p>
              </div>

              {/* Source and Scope row */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Source:
                  </span>{" "}
                  <span
                    className={`text-xs font-medium px-2.5 py-0.5 rounded ${getSourceTypeColorAndLabel(selectedRule.sourceType).style}`}
                  >
                    {getSourceTypeColorAndLabel(selectedRule.sourceType).label}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Scope:
                  </span>{" "}
                  <span
                    className={`text-xs font-medium px-2.5 py-1 rounded inline-block ${
                      selectedRule.scopeTargetType === "user"
                        ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300"
                        : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                    }`}
                  >
                    <div className="text-center">
                      {selectedRule.scopeTargetType || "global"}
                    </div>
                    {user?.isSuperAdmin && selectedRule.scopeTargetDetails && (
                      <div className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                        {selectedRule.scopeTargetDetails.firstName}{" "}
                        {selectedRule.scopeTargetDetails.lastName}
                        <br />
                        {selectedRule.scopeTargetDetails.email}
                      </div>
                    )}
                  </span>
                </div>
              </div>

              {/* Timestamp row */}
              <div className="mb-6">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Created:
                </span>{" "}
                <span className="text-sm text-gray-900 dark:text-gray-100">
                  {new Date(selectedRule.createdAt).toLocaleString()}
                </span>
              </div>

              {/* Recent Matches table */}
              <div className="border-t pt-4">
                <h4 className="text-lg font-semibold mb-3">Recent Matches</h4>
                {isLoadingMatches ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Loading matches...
                  </div>
                ) : ruleMatches && ruleMatches.messages.length > 0 ? (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {ruleMatches.messages.map((match) => (
                      <div
                        key={match.id}
                        className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                              {match.message.subject}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              From: {match.message.from[0]?.address}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Inbox: {match.message.inbox.email}
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(
                                match.message.timestamp,
                              ).toLocaleString()}
                            </div>
                            <div
                              className={`text-xs font-medium px-1 py-0.5 rounded mt-1 inline-block ${
                                match.actionType === "challenge"
                                  ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                                  : match.actionType === "allow"
                                    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                                    : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                              }`}
                            >
                              {getActionDisplayText(match.actionType)}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {match.selectedRuleId !== selectedRule.id && (
                                <span className="text-orange-600 dark:text-orange-400">
                                  this rule matched but was superseded by a rule
                                  with higher precedence
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    No recent matches found for this rule.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Import Response Modal */}
        {importResponse && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setImportResponse(null)}
          >
            <div
              className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-2xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Import Results</h3>
                <button
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  onClick={() => setImportResponse(null)}
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded overflow-auto max-h-96">
                {JSON.stringify(importResponse, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Delete All Confirmation Modal */}
        {isDeleteModalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setIsDeleteModalOpen(false)}
          >
            <div
              className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-red-600 dark:text-red-400">
                  Delete All Rules
                </h3>
                <button
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  onClick={() => setIsDeleteModalOpen(false)}
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="mb-6">
                <p className="text-gray-700 dark:text-gray-300">
                  This will delete all of your Cobutler rules, use at your own
                  risk!
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-400 dark:hover:bg-gray-600"
                >
                  Never mind!
                </button>
                <button
                  onClick={handleDeleteAll}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Do it
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Alias/Mailing List Modal */}
        {isAliasModalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setIsAliasModalOpen(false)}
          >
            <div
              className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Allow Email Forwarded via Alias/List
                </h3>
                <button
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  onClick={() => setIsAliasModalOpen(false)}
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="mb-6">
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  If you receive email via mailing lists or Gmail aliases and
                  you want to ensure that{" "}
                  <span className="font-semibold">every</span> email addressed
                  to that list/alias goes to your inbox.
                </p>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address or Domain
                </label>
                <input
                  type="text"
                  value={aliasInput}
                  onChange={(e) => setAliasInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleCreateAliasRule();
                    }
                  }}
                  placeholder="e.g., support@company.com or company.com"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                  autoFocus
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Enter an email address (with @) or just a domain to match all
                  addresses at that domain
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setIsAliasModalOpen(false)}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-400 dark:hover:bg-gray-600"
                  disabled={isCreatingRule}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateAliasRule}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isCreatingRule || !aliasInput.trim()}
                >
                  {isCreatingRule ? "Creating..." : "Create Rule"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Domain Rule Modal */}
        {isDomainModalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setIsDomainModalOpen(false)}
          >
            <div
              className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Allow Email from Domain
                </h3>
                <button
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  onClick={() => setIsDomainModalOpen(false)}
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="mb-6">
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Allow all emails <span className="font-semibold">from</span> a
                  specific domain to go to your inbox.
                </p>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Domain
                </label>
                <input
                  type="text"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleCreateDomainRule();
                    }
                  }}
                  placeholder="e.g., example.com"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                  autoFocus
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Enter a domain (the @ symbol will be added automatically if
                  needed)
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setIsDomainModalOpen(false)}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-400 dark:hover:bg-gray-600"
                  disabled={isCreatingRule}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateDomainRule}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isCreatingRule || !domainInput.trim()}
                >
                  {isCreatingRule ? "Creating..." : "Create Rule"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default RulesTable;
